import React, { useEffect, useState } from "react";
import { api, downloadCsv } from "@/lib/api";
import { Loading, Card, PageHeader, EmptyState } from "@/components/common";
import { toast } from "sonner";
import { FileBarChart, Download, Eye, Filter, X } from "lucide-react";

const inputCls = "h-9 rounded-lg bg-white border border-brand-line px-2 text-sm text-brand-ink outline-none focus:border-brand-civic w-full";

// Maps a report's declared filter keys to concrete controls.
const CONTROLS = {
  date_range: { type: "date_range" },
  chamber: { type: "select", opts: "chambers", label: "Chamber" },
  session: { type: "session", label: "Session" },
  committee: { type: "list", opts: "committees", label: "Committee" },
  policy_area: { type: "list", opts: "policy_areas", label: "Policy Area" },
  policy_unit: { type: "list", opts: "policy_units", label: "Policy Unit" },
  assignment_group: { type: "list", opts: "assignment_groups", label: "Assignment Group" },
  assigned_user: { type: "users", label: "Assigned User" },
  workflow_status: { type: "wf", label: "Workflow Status" },
  priority: { type: "select", opts: "priorities", label: "Priority" },
  event_type: { type: "list", opts: "event_types", label: "Event Type" },
};

function buildQuery(f) {
  const p = new URLSearchParams();
  Object.entries(f).forEach(([k, v]) => { if (v) p.append(k, v); });
  const s = p.toString();
  return s ? `?${s}` : "";
}

export default function Reports() {
  const [catalog, setCatalog] = useState(null);
  const [active, setActive] = useState(null);
  const [report, setReport] = useState(null);
  const [filters, setFilters] = useState({});

  useEffect(() => { api.get("/reports/catalog").then((r) => setCatalog(r.data)).catch(() => setCatalog(false)); }, []);

  const activeMeta = catalog?.reports?.find((r) => r.key === active);
  const opts = catalog?.filter_options || {};

  const view = (key, f = filters) => { setActive(key); setReport(null); api.get(`/reports/${key}${buildQuery(f)}`).then((r) => setReport(r.data)).catch(() => setReport(false)); };
  const exportCsv = async (key, f = filters) => { try { await downloadCsv(`/reports/${key}/export.csv${buildQuery(f)}`, `${key}.csv`); toast.success("CSV exported"); } catch { toast.error("Export failed"); } };
  const selectReport = (key) => { setFilters({}); view(key, {}); };
  const setF = (k, v) => setFilters((prev) => ({ ...prev, [k]: v || undefined }));
  const clearFilters = () => { setFilters({}); view(active, {}); };

  if (catalog === null) return <Loading />;
  if (!catalog) return <EmptyState icon={FileBarChart} title="Unable to load reports" />;

  const supported = activeMeta?.filters || [];

  return (
    <div>
      <PageHeader title="Reports" subtitle="Operational legislative reports with filters and CSV export. Data-mode isolation and access controls are always enforced." />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 space-y-2 max-h-[80vh] overflow-y-auto pr-1">
          {catalog.reports.map((r) => (
            <div key={r.key} className={`rounded-xl border p-3 flex items-center justify-between gap-2 shadow-card transition-colors ${active === r.key ? "border-brand-civic bg-brand-civic-50" : "border-brand-line bg-white hover:border-brand-civic/40"}`} data-testid={`report-${r.key}`}>
              <button onClick={() => selectReport(r.key)} className="text-sm font-medium text-brand-ink text-left flex-1" data-testid={`view-report-${r.key}`}>{r.name}</button>
              <button onClick={() => exportCsv(r.key)} data-testid={`export-report-${r.key}`} className="h-8 w-8 rounded-lg hover:bg-white flex items-center justify-center text-brand-civic shrink-0" aria-label="Export CSV"><Download className="h-4 w-4" /></button>
            </div>
          ))}
        </div>

        <div className="lg:col-span-2">
          {active === null ? <EmptyState icon={FileBarChart} title="Select a report" description="Choose a report to preview its data, apply filters, then export to CSV." /> : (
            <>
              {supported.length > 0 && (
                <Card className="mb-4" data-testid="report-filters">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2"><Filter className="h-4 w-4 text-brand-civic" /><h3 className="font-display font-semibold text-brand-ink text-sm">Filters</h3></div>
                    {Object.keys(filters).length > 0 && <button onClick={clearFilters} className="text-xs text-slate-400 hover:text-brand-navy inline-flex items-center gap-1"><X className="h-3 w-3" /> Clear</button>}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {supported.map((fk) => {
                      const ctrl = CONTROLS[fk];
                      if (!ctrl) return null;
                      if (ctrl.type === "date_range") return (
                        <React.Fragment key={fk}>
                          <div><label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">From</label><input type="date" data-testid="filter-date-from" value={filters.date_from || ""} onChange={(e) => setF("date_from", e.target.value)} className={inputCls} /></div>
                          <div><label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">To</label><input type="date" data-testid="filter-date-to" value={filters.date_to || ""} onChange={(e) => setF("date_to", e.target.value)} className={inputCls} /></div>
                        </React.Fragment>
                      );
                      let list = [], render = (o) => o, valOf = (o) => o;
                      if (ctrl.type === "select" || ctrl.type === "list") list = opts[ctrl.opts] || [];
                      else if (ctrl.type === "wf") list = opts.workflow_statuses || [];
                      else if (ctrl.type === "session") { list = opts.sessions || []; render = (o) => o.name; valOf = (o) => o.session_id; }
                      else if (ctrl.type === "users") { list = opts.users || []; render = (o) => o.name; valOf = (o) => o.user_id; }
                      const key = ctrl.type === "session" ? "session" : ctrl.type === "users" ? "assigned_user" : fk;
                      return (
                        <div key={fk}>
                          <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{ctrl.label}</label>
                          <select data-testid={`filter-${fk}`} value={filters[key] || ""} onChange={(e) => setF(key, e.target.value)} className={inputCls}>
                            <option value="">All</option>
                            {list.map((o, i) => <option key={i} value={valOf(o)}>{String(render(o)).replace(/_/g, " ")}</option>)}
                          </select>
                        </div>
                      );
                    })}
                  </div>
                  <button onClick={() => view(active)} data-testid="apply-filters" className="mt-3 inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-brand-navy text-white text-sm font-semibold hover:bg-brand-navy-700">Apply filters</button>
                </Card>
              )}

              {report === null ? <Loading /> : !report ? <EmptyState title="Failed to load report" /> : (
                <Card data-testid="report-preview">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-display font-semibold text-brand-ink">{report.name} <span className="text-slate-400 text-sm font-normal">({report.count} rows)</span></h3>
                    <button onClick={() => exportCsv(report.key)} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-brand-navy text-white text-sm font-semibold hover:bg-brand-navy-700 transition-colors" data-testid="export-current-report"><Download className="h-4 w-4" /> Export CSV</button>
                  </div>
                  {report.count === 0 ? <p className="text-sm text-slate-400 py-6 text-center">No rows match the current filters and data mode.</p> : (
                    <div className="overflow-x-auto max-h-[60vh] rounded-lg border border-brand-line">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-500 text-xs uppercase sticky top-0 border-b border-brand-line"><tr>{report.columns.map((c) => <th key={c} className="text-left px-3 py-2 whitespace-nowrap font-semibold">{c.replace(/_/g, " ")}</th>)}</tr></thead>
                        <tbody className="divide-y divide-brand-line">
                          {report.rows.map((row, i) => <tr key={i} className="hover:bg-slate-50 transition-colors">{report.columns.map((c) => <td key={c} className="px-3 py-2 text-slate-600 whitespace-nowrap">{String(row[c] ?? "—").slice(0, 60)}</td>)}</tr>)}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
